import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { formatMoneyPeriodHeader, getPeriodKey } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    ExpenseByVendorSort,
    periodSortAsc,
    periodSortDesc,
} from "@common/helpers/expenseByVendorSort";
import { ViewConfig } from "@lib/views";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import {
    getColumnPinOptions,
    getLabelColumnMaxSize,
} from "../singleColumnReport";
import { makeExpenseByVendorPeriodCell } from "./ExpenseByVendorPeriodCell";
import { ExpenseByVendorViewRow } from "./expenseByVendorViewTypes";
import styles from "./ExpenseByVendorTable.module.scss";

const EBV_TITLE_COLUMN_ID = "vendor";
const TITLE_MIN_SIZE = 240;
const TITLE_MAX_SIZE = 1200;

function partitionPeriods(periods: ReportPeriodWithLabel[]) {
    const nonTotalPeriods: ReportPeriodWithLabel[] = [];
    const totalPeriods: ReportPeriodWithLabel[] = [];
    for (const period of periods) {
        if (period.isTotal) {
            totalPeriods.push(period);
        } else {
            nonTotalPeriods.push(period);
        }
    }
    return { nonTotalPeriods, totalPeriods };
}

// ---------------------------------------------------------------------------
// Title cell
// ---------------------------------------------------------------------------

const renderVendorTitleCell = React.memo(function ExpenseByVendorTitleCell({
    row,
}: CellContext<
    ViewTableFeatures,
    ExpenseByVendorViewRow,
    unknown
>): React.ReactElement {
    const { vendorName, isSummary } = row.original;
    return (
        <ViewTableCell className={isSummary ? styles.summaryCell : undefined}>
            {vendorName}
        </ViewTableCell>
    );
});

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

type ExpenseByVendorColumnRecord = Record<
    string,
    ViewConfig<
        ExpenseByVendorViewRow,
        string,
        ExpenseByVendorSort
    >["columns"][string]
>;

/**
 * Builds an ephemeral ViewConfig driven entirely by the period list.
 * Uses string as TColumn since column IDs are dynamically derived from periods.
 *
 * Unlike P&L / Balance Sheet, TSort is a real enum (`ExpenseByVendorSort`)
 * because this report is sortable.  ViewTable does not feed sorted data
 * through TanStack's `getSortedRowModel`; the sort props only drive the
 * `SortableHeader` caret state.  The page-level table component pre-sorts
 * the rows via `sortExpenseByVendorRows` before passing them to ViewTable.
 */
export function buildExpenseByVendorConfig(
    periods: ReportPeriodWithLabel[],
    numberFormat?: NumberFormatValues,
): ViewConfig<ExpenseByVendorViewRow, string, ExpenseByVendorSort> {
    const columns: ExpenseByVendorColumnRecord = {
        [EBV_TITLE_COLUMN_ID]: {
            name: "Vendor",
            header: "Vendor",
            accessorKey: "vendorName",
            cell: renderVendorTitleCell,
            minSize: TITLE_MIN_SIZE,
            maxSize: getLabelColumnMaxSize({
                periods,
                labelMaxSize: TITLE_MAX_SIZE,
            }),
            immutable: true,
            sort: {
                asc: ExpenseByVendorSort.VENDOR_ASC,
                desc: ExpenseByVendorSort.VENDOR_DESC,
            },
        },
    };

    periods.forEach((period, periodIndex) => {
        const isTotalPeriod = Boolean(period.isTotal);
        const columnId = isTotalPeriod
            ? `total-${getPeriodKey(period)}`
            : `period-${getPeriodKey(period)}`;

        columns[columnId] = {
            name: period.label,
            header: formatMoneyPeriodHeader(period, numberFormat),
            accessorKey: "values",
            cell: makeExpenseByVendorPeriodCell({ period, periodIndex }),
            ...AMOUNT_COLUMN_SIZE,
            sort: {
                asc: periodSortAsc(periodIndex),
                desc: periodSortDesc(periodIndex),
            },
        };
    });

    return { columns };
}

/**
 * Builds an ephemeral View object (never persisted to DB).
 * Vendor column is pinned left; total period columns are pinned right.
 */
export function buildExpenseByVendorView(
    periods: ReportPeriodWithLabel[],
): View {
    const { nonTotalPeriods, totalPeriods } = partitionPeriods(periods);

    return {
        type: ViewType.EXPENSE_BY_VENDOR,
        columns: [
            {
                id: EBV_TITLE_COLUMN_ID,
                options: getColumnPinOptions({ periods, pin: "left" }),
            },
            ...nonTotalPeriods.map((p) => ({
                id: `period-${getPeriodKey(p)}`,
            })),
            ...totalPeriods.map((p) => ({
                id: `total-${getPeriodKey(p)}`,
                options: getColumnPinOptions({ periods, pin: "right" }),
            })),
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
